import { useAction } from "convex/react"
import { memo, useCallback, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useAppAuth } from "@/hooks/useAppAuth"
import { useVideoPlayback } from "@/hooks/useVideoPlayback"
import { useWebBalance } from "@/hooks/useWebBalance"
import { api } from "~convex/_generated/api"
import type { EnrichedPost } from "~convex/queryHelpers"
import { extractFirstCashtag } from "~shared/cashtag"
import { r2ImageUrl, r2RawUrl } from "~shared/r2"
import { Avatar } from "./Avatar"
import { CashtagBuyModal } from "./CashtagBuyModal"
import { EngagementBar } from "./EngagementBar"
import { ImageLightbox } from "./ImageLightbox"
import { LazyComposeModal, preloadComposeModal } from "./LazyComposeModal"
import { LinkifiedText } from "./LinkifiedText"
import { PostMenu } from "./PostMenu"
import { SourceBadge } from "./SourceBadge"
import { SpotlightBadge } from "./SpotlightBadge"
import { TimeAgo } from "./TimeAgo"

interface PostCardProps {
	onReply?: () => void
	post: EnrichedPost
}

const TREND_CONTENT_PREVIEW_LENGTH = 280

export const PostCard = memo(function PostCard({ onReply, post }: PostCardProps) {
	const navigate = useNavigate()
	const [lightboxOpen, setLightboxOpen] = useState(false)
	const [imageFallback, setImageFallback] = useState(false)
	const [quoteOpen, setQuoteOpen] = useState(false)
	const [trendExpanded, setTrendExpanded] = useState(false)
	const { isAuthenticated } = useAppAuth()
	const videoRef = useVideoPlayback()
	const isRepost = post.type === "repost" && post.originalPost
	const displayPost = isRepost && post.originalPost ? post.originalPost : post
	const displayAuthor = isRepost ? post.originalAuthor : post.author
	const content = displayPost.content
	const shouldCollapseTrendContent =
		displayPost.source === "pump" && content.length > TREND_CONTENT_PREVIEW_LENGTH && !trendExpanded
	const trendContentText = shouldCollapseTrendContent
		? content.slice(0, TREND_CONTENT_PREVIEW_LENGTH).trimEnd()
		: content
	const cashtag = useMemo(() => extractFirstCashtag(content), [content])
	const communityToken = useMemo(
		() =>
			displayPost.community_ticker && displayPost.community_mint
				? { ticker: displayPost.community_ticker, mint: displayPost.community_mint }
				: null,
		[displayPost.community_ticker, displayPost.community_mint],
	)
	const { usdcBalance } = useWebBalance()
	const [buyModalTarget, setBuyModalTarget] = useState<{
		ticker: string
		mint?: string
		defaultAmount?: number
		autoExecute?: boolean
	} | null>(null)
	const fetchTokenInfo = useAction(api.swapActions.getTokenInfo)
	const fetchTokenInfoByMint = useAction(api.swapActions.getTokenInfoByMint)

	const handleCashtagAction = useCallback(
		(ticker: string) => {
			if (usdcBalance <= 0) {
				Promise.resolve(navigate("/wallet", { state: { addFunds: true } })).catch(() => {})
				return
			}
			fetchTokenInfo({ ticker })
				.then(info => {
					if (!info) {
						toast("Cashtag not found")
						return
					}
					setBuyModalTarget({ ticker })
				})
				.catch(() => toast("Cashtag not found"))
		},
		[usdcBalance, navigate, fetchTokenInfo],
	)

	const handleCashtagClick = useCallback(
		(ticker: string) => {
			handleCashtagAction(ticker)
		},
		[handleCashtagAction],
	)

	const handleTokenAddressClick = useCallback(
		(mint: string) => {
			if (usdcBalance <= 0) {
				Promise.resolve(navigate("/wallet", { state: { addFunds: true } })).catch(() => {})
				return
			}
			fetchTokenInfoByMint({ mint })
				.then(info => {
					if (!info) {
						toast("Token not found")
						return
					}
					setBuyModalTarget({ ticker: info.ticker, mint })
				})
				.catch(() => toast("Token not found"))
		},
		[usdcBalance, navigate, fetchTokenInfoByMint],
	)

	const handleTradeButton = useCallback(
		(ticker: string) => {
			if (cashtag) {
				handleCashtagAction(ticker)
				return
			}
			if (!communityToken) {
				return
			}
			if (usdcBalance <= 0) {
				Promise.resolve(navigate("/wallet", { state: { addFunds: true } })).catch(() => {})
				return
			}
			setBuyModalTarget({ ticker: communityToken.ticker, mint: communityToken.mint })
		},
		[cashtag, communityToken, usdcBalance, navigate, handleCashtagAction],
	)

	if (!displayAuthor) {
		return null
	}

	const handleCardClick = (e: React.MouseEvent) => {
		const target = e.target as HTMLElement
		// Don't navigate if clicking a link, button, or interactive element
		if (target.closest("a, button, [role=button], input, textarea")) {
			return
		}
		// Don't navigate if user is selecting text
		const selection = window.getSelection()
		if (selection && selection.toString().length > 0) {
			return
		}
		Promise.resolve(navigate(`/post/${displayPost._id}`, { state: { post } })).catch(() => {})
	}

	return (
		<article
			className="animate-fade-in cursor-pointer border-app-border border-b px-3 py-3 transition-colors duration-150 hover:bg-app-surface sm:px-4"
			onClick={handleCardClick}
			onKeyDown={e => {
				if (e.key === "Enter" && !(e.target as HTMLElement).closest("a, button")) {
					handleCardClick(e as unknown as React.MouseEvent)
				}
			}}
		>
			{(() => {
				const src = isRepost && post.originalPost ? post.originalPost : post
				return src.community_name && src.community_ticker ? (
					<Link
						className="mb-1 flex items-center gap-1.5 text-app-text-secondary text-xs hover:text-app-text"
						onClick={(e: React.MouseEvent) => e.stopPropagation()}
						to={`/community/${src.community_ticker}`}
					>
						<svg
							aria-hidden="true"
							className="h-3 w-3"
							fill="none"
							stroke="currentColor"
							strokeWidth={2}
							viewBox="0 0 24 24"
						>
							<path
								d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-4a4 4 0 100-8 4 4 0 000 8zm8 0a3 3 0 100-6 3 3 0 000 6zM5 10a3 3 0 100-6 3 3 0 000 6z"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<span className="font-semibold tracking-wide">{src.community_name.toUpperCase()}</span>
					</Link>
				) : null
			})()}

			{isRepost && post.author && (
				<div className="mb-1 ml-10 flex items-center gap-1.5 text-app-text-secondary text-xs sm:ml-12">
					<svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path d="M17 1l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
						<path
							d="M3 11V9a4 4 0 0 1 4-4h14"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
						/>
					</svg>
					<span>{post.author.display_name || post.author.handle} reposted</span>
				</div>
			)}

			{post.is_spotlight && (
				<div className="mb-2">
					<SpotlightBadge />
				</div>
			)}

			<div className="flex gap-3">
				<Link
					className="shrink-0"
					onClick={(e: React.MouseEvent) => e.stopPropagation()}
					to={`/user/${displayAuthor.handle}`}
				>
					<Avatar
						alt={displayAuthor.handle}
						fallback={displayAuthor.handle[0]?.toUpperCase() ?? ""}
						size="md"
						style={{ viewTransitionName: `post-avatar-${displayPost._id}` }}
						url={displayAuthor.avatar_url}
					/>
				</Link>

				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-1.5 text-sm">
						<Link
							className="truncate font-semibold text-app-text hover:underline"
							onClick={(e: React.MouseEvent) => e.stopPropagation()}
							to={`/user/${displayAuthor.handle}`}
						>
							{displayAuthor.display_name || displayAuthor.handle}
						</Link>
						<SourceBadge source={displayPost.source} />
						<span className="text-app-text-secondary">@{displayAuthor.handle}</span>
						<span className="text-app-text-tertiary">·</span>
						<TimeAgo timestamp={displayPost._creationTime} />
						{isAuthenticated && displayAuthor && (
							<span className="ml-auto">
								<PostMenu
									authorHandle={displayAuthor.handle}
									authorId={displayPost.author_id}
									isSpotlight={!!displayPost.is_spotlight}
									postId={displayPost._id}
								/>
							</span>
						)}
					</div>

					{content && (
						<div className="mt-1 whitespace-pre-wrap break-words text-[15px] text-app-text leading-snug">
							<LinkifiedText
								onCashtagClick={handleCashtagClick}
								onTokenAddressClick={handleTokenAddressClick}
								text={trendContentText}
							/>
							{shouldCollapseTrendContent ? (
								<button
									className="font-bold text-app-text hover:underline"
									onClick={(e: React.MouseEvent) => {
										e.preventDefault()
										e.stopPropagation()
										setTrendExpanded(true)
									}}
									type="button"
								>
									{" Show more"}
								</button>
							) : null}
						</div>
					)}

					{displayPost.image_url && (
						<>
							<button
								aria-label="View full image"
								className="mt-2 block w-full cursor-zoom-in appearance-none border-0 bg-transparent p-0"
								onClick={(e: React.MouseEvent) => {
									e.preventDefault()
									e.stopPropagation()
									setLightboxOpen(true)
								}}
								type="button"
							>
								<img
									alt=""
									className="max-h-[400px] w-full rounded-xl object-cover"
									loading="lazy"
									onError={() => {
										if (!imageFallback) {
											setImageFallback(true)
										}
									}}
									src={
										imageFallback
											? r2RawUrl(displayPost.image_url)
											: r2ImageUrl(displayPost.image_url, { width: 600 })
									}
									style={
										displayPost.image_width && displayPost.image_height
											? { aspectRatio: `${displayPost.image_width} / ${displayPost.image_height}` }
											: undefined
									}
								/>
							</button>
							{lightboxOpen &&
								createPortal(
									<ImageLightbox
										alt={displayPost.content ?? ""}
										imageUrl={displayPost.image_url}
										onClose={() => setLightboxOpen(false)}
									/>,
									document.body,
								)}
						</>
					)}

					{displayPost.video_url && (
						<video
							autoPlay
							className="mt-2 max-h-[250px] w-full rounded-xl bg-app-surface sm:max-h-[400px]"
							controls
							loop
							muted
							playsInline
							preload="metadata"
							ref={videoRef}
							src={r2RawUrl(displayPost.video_url)}
							style={{
								aspectRatio:
									displayPost.video_width && displayPost.video_height
										? `${displayPost.video_width} / ${displayPost.video_height}`
										: "16 / 9",
							}}
						/>
					)}

					{post.quotedPost && (
						<Link
							className="mt-2 block w-full rounded-xl border border-app-border p-3 text-inherit no-underline"
							onClick={(e: React.MouseEvent) => e.stopPropagation()}
							to={`/post/${post.quotedPost._id}`}
						>
							<div className="flex items-center gap-1.5 text-sm">
								<span className="font-semibold text-app-text">
									{post.quotedAuthor?.display_name || post.quotedAuthor?.handle}
								</span>
								<span className="text-app-text-secondary">@{post.quotedAuthor?.handle}</span>
							</div>
							<div className="mt-1 text-app-text text-sm">{post.quotedPost.content}</div>
						</Link>
					)}

					{(() => {
						const src = isRepost && post.originalPost ? post.originalPost : post
						const communityTicker = src.community_ticker
						const communityMint = src.community_mint
						const handleInstantBuy =
							communityTicker && communityMint
								? () => {
										if (usdcBalance <= 0) {
											Promise.resolve(navigate("/wallet", { state: { addFunds: true } })).catch(
												() => {},
											)
											return
										}
										setBuyModalTarget({
											autoExecute: true,
											defaultAmount: 10,
											mint: communityMint,
											ticker: communityTicker,
										})
									}
								: undefined
						return (
							<EngagementBar
								captchaUsd={displayPost.captcha_usd ?? 1}
								cashtag={cashtag ?? communityToken?.ticker}
								likeCount={displayPost.like_count}
								liked={post.liked}
								onCashtagBuy={handleTradeButton}
								onInstantBuy={handleInstantBuy}
								onQuote={() => {
									preloadComposeModal()
									setQuoteOpen(true)
								}}
								onReply={onReply}
								postId={displayPost._id}
								replyCount={displayPost.reply_count}
								repostCount={displayPost.repost_count}
								reposted={post.reposted}
							/>
						)
					})()}
				</div>
			</div>
			{quoteOpen && (
				<LazyComposeModal
					mode="quote"
					onClose={() => setQuoteOpen(false)}
					quotePostId={displayPost._id}
				/>
			)}
			{buyModalTarget && (
				<CashtagBuyModal
					autoExecute={buyModalTarget.autoExecute}
					defaultAmount={buyModalTarget.defaultAmount}
					onClose={() => setBuyModalTarget(null)}
					ticker={buyModalTarget.ticker}
					tokenMint={buyModalTarget.mint}
				/>
			)}
		</article>
	)
})
